import React, { FC } from "react";

import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import WarningBox from "../../../../common/components/box/WarningBox";
import { useDialog } from "../../../../common/components/dialog/useDialog";
import MainPaperWrapper from "../../../../common/components/item/MainPaperWrapper";
import { allModelsOptions } from "../../../../common/components/select/ServiceSelect/ModelsToRunMultiselect";
import { ServiceTypeEnum } from "../../../model-manager/variables/modelManager";
import { IDeviceTableRow } from "../../hooks/useDeviceTableRows";
import DeviceModels from "./DeviceModels";
import AlertModelSettings from "./model-settings/AlertModelSettings";
import DraggedModalBody from "./model-settings/DraggedModalBody";

interface IDeviceModelProps {
  device: IDeviceTableRow;
}

const DeviceModelsContainer: FC<IDeviceModelProps> = ({
  device,
}): JSX.Element => {
  const { open, handleDialogClose, handleDialogOpen } = useDialog();

  const model = allModelsOptions.find((option): boolean => {
    return device.modelsToRun.some(
      (modelToRun): boolean =>
        modelToRun === option.value || modelToRun === option.name
    );
  });

  const safetySecurityModel = device?.models.find(
    (model): boolean =>
      model.serviceName === ServiceTypeEnum.SAFETY_AND_SECURITY ||
      model.serviceName === ServiceTypeEnum.PERSONNEL_DETECTION
  );

  const closeGuardRef = React.useRef<(doClose: () => void) => void>(doClose =>
    doClose()
  );

  const guardedClose = React.useCallback(() => {
    closeGuardRef.current(() => handleDialogClose());
  }, [handleDialogClose]);

  if (!model) {
    return <></>;
  }

  return (
    <MainPaperWrapper>
      <Stack direction="column" spacing={2}>
        <Typography variant="h5">Models</Typography>

        {!device.isOnline && (
          <WarningBox>
            The device is offline now. Models will be monitored when the device
            is online
          </WarningBox>
        )}

        <DeviceModels device={device} handleDialogOpen={handleDialogOpen} />
      </Stack>

      {safetySecurityModel?.modelAlertSettings && (
        <DraggedModalBody
          title="Model settings"
          open={open}
          persistId="model-alerting-settings"
          onClose={guardedClose}
        >
          <AlertModelSettings
            locationId={device.location.id}
            nodeId={device.node.id}
            modelAlertSettings={safetySecurityModel.modelAlertSettings}
            setCloseGuard={guardFn => {
              closeGuardRef.current = guardFn;
            }}
          />
        </DraggedModalBody>
      )}
    </MainPaperWrapper>
  );
};

export default React.memo(DeviceModelsContainer);
